"use client";

import * as React from "react";
import { formatDistanceToNow } from "date-fns";
import { Check, Newspaper, AlertCircle, CheckCircle, Info } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/shadcn/button";
import type { NotificationItemProps } from "@/types/notifications";

export function NotificationItem({ notification, onMarkAsRead, onClick }: NotificationItemProps) {
  const isRead = notification.readAt !== null;
  const isNews = notification.kind === "news";
  const isToast = notification.kind === "toast";

  // Get icon based on notification type and meta
  const getIcon = () => {
    if (isNews) {
      const severity = notification.meta?.kind === "news" ? notification.meta.severity : null;
      if (severity === "critical") {
        return <AlertCircle className="h-5 w-5 text-red-400" />;
      }
      if (severity === "warning") {
        return <AlertCircle className="h-5 w-5 text-yellow-400" />;
      }
      return <Newspaper className="h-5 w-5 text-blue-400" />;
    }

    if (isToast && notification.meta?.kind === "toast") {
      const tone = notification.meta.tone;
      if (tone === "error") {
        return <AlertCircle className="h-5 w-5 text-red-400" />;
      }
      if (tone === "success") {
        return <CheckCircle className="h-5 w-5 text-green-400" />;
      }
      return <Info className="h-5 w-5 text-blue-400" />;
    }

    return <Info className="h-5 w-5 text-muted-foreground" />;
  };

  const timeAgo = formatDistanceToNow(notification.createdAt, { addSuffix: true });

  const handleClick = () => {
    // Only news notifications are clickable (to open modal)
    if (isNews && onClick) {
      onClick(notification);
    }
  };

  return (
    <div
      className={cn(
        "group relative flex gap-2 sm:gap-3 rounded-md p-2 sm:p-3 transition-all hover:bg-muted/50",
        isNews && onClick && "cursor-pointer"
      )}
      onClick={handleClick}
    >
      {/* Unread indicator dot - only for news */}
      {!isRead && isNews && (
        <div className="absolute left-0.5 top-1/2 h-1.5 w-1.5 -translate-y-1/2 rounded-full bg-blue-400" />
      )}

      {/* Icon */}
      <div className="flex-shrink-0 pt-0.5">
        <div className="h-4 w-4 sm:h-5 sm:w-5 [&>svg]:h-4 [&>svg]:w-4 sm:[&>svg]:h-5 sm:[&>svg]:w-5">
          {getIcon()}
        </div>
      </div>

      {/* Content */}
      <div className="min-w-0 flex-1 space-y-0.5 sm:space-y-1">
        {/* Title (news only) */}
        {notification.title && (
          <h4 className="text-xs sm:text-sm font-medium text-foreground leading-snug">
            {notification.title}
          </h4>
        )}

        {/* Message preview - full text for toast, truncated for news */}
        <p
          className={cn(
            "text-xs sm:text-sm leading-relaxed",
            notification.title ? "text-muted-foreground line-clamp-2" : "text-foreground"
          )}
        >
          {notification.messagePreview}
        </p>

        {/* Timestamp */}
        <p className="text-[10px] sm:text-xs text-muted-foreground">{timeAgo}</p>
      </div>

      {/* Mark as read button - only for news */}
      {!isRead && isNews && onMarkAsRead && (
        <div className="flex-shrink-0">
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 sm:h-7 sm:w-7 opacity-0 transition-opacity group-hover:opacity-100 text-muted-foreground hover:text-foreground"
            onClick={(e) => {
              e.stopPropagation();
              onMarkAsRead(notification.id);
            }}
            aria-label="Mark as read"
          >
            <Check className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
}
